import { libraryPath } from '@/features/exams/library-params'
import type { LockReason, MyTasks, TaskItem, TaskLock, TaskStatus } from './tasks-api'

/** What a locked test page needs to say: why, which paper finishes it, and when it is due. */
export interface LockNotice {
  reason: LockReason
  requiredTestId: string | null
  dueAt: Date | null
}

/**
 * Is starting `testId` blocked right now? `null` when nothing locks, when the test is one that finishes the locking task
 * (`allowed_test_ids`), or when a sitting on it is already running (`active_test_ids`, or `activeRunId`: the library's own
 * knowledge of a run in progress). Ids compare as strings, so a route param and an API number agree.
 */
export function lockFor(lock: TaskLock | null, testId: string | number, activeRunId?: string | null): LockNotice | null {
  if (!lock || activeRunId) return null
  const id = String(testId)
  if (lock.allowedTestIds.includes(id) || lock.activeTestIds.includes(id)) return null
  return { reason: lock.reason, requiredTestId: lock.requiredTestId, dueAt: lock.dueAt }
}

const OPEN: readonly TaskStatus[] = ['pending', 'in_progress', 'overdue']
export const isOpenStatus = (status: TaskStatus): boolean => OPEN.includes(status)

const byDue = (a: TaskItem, b: TaskItem): number => {
  const x = a.dueAt?.getTime() ?? Number.POSITIVE_INFINITY
  const y = b.dueAt?.getTime() ?? Number.POSITIVE_INFINITY
  return x - y || a.assignedAt.getTime() - b.assignedAt.getTime()
}

/** Open tasks, overdue first, then the soonest due; a task with no due date last. */
export function openTasks(tasks: readonly TaskItem[]): TaskItem[] {
  return tasks
    .filter((task) => isOpenStatus(task.status))
    .sort((a, b) => Number(b.status === 'overdue') - Number(a.status === 'overdue') || byDue(a, b))
}

/** Finished or waived tasks, the latest first. */
export function doneTasks(tasks: readonly TaskItem[]): TaskItem[] {
  return tasks
    .filter((task) => !isOpenStatus(task.status))
    .sort((a, b) => (b.completedAt?.getTime() ?? b.assignedAt.getTime()) - (a.completedAt?.getTime() ?? a.assignedAt.getTime()))
}

type Pointer = Pick<TaskItem, 'kind' | 'skill' | 'test'>

/** True when the student has a sitting running on the paper this task points at: the call to action is Resume, not Start. */
export function isResume(task: Pick<Pointer, 'test'>, activeTestIds: readonly string[]): boolean {
  return task.test !== null && activeTestIds.includes(task.test.id)
}

/**
 * Where a task's button goes. A running sitting on its paper first; else its paper when that paper is available now; else the
 * library filtered to what the task asks for (full mocks, or the one skill), because a paper that is no longer available
 * must never be linked (it would open a 404 in the runner).
 */
export function taskStartPath(task: Pointer, activeTestIds: readonly string[] = []): string {
  if (task.test && (isResume(task, activeTestIds) || task.test.available)) return `/test/${encodeURIComponent(task.test.id)}`
  return libraryFor(task)
}

export function libraryFor(task: Pick<Pointer, 'kind' | 'skill'>): string {
  if (task.kind === 'section' && task.skill) return libraryPath({ testType: task.skill })
  if (task.kind === 'full_mock') return libraryPath({ testType: 'full' })
  return libraryPath({})
}

/** The task a lock is for, so a locked page can send the student to the right library filter. */
export function lockedItem(data: MyTasks): TaskItem | null {
  const lock = data.lock
  if (!lock) return null
  return data.tasks.find((candidate) => candidate.targetId === lock.targetId) ?? null
}

/**
 * The welcome test a teacher chose for every new student of the school, while it is still open. At most one exists; the Home card
 * shows it first and the welcome page is about it.
 */
export function onboardingTask(tasks: readonly TaskItem[]): TaskItem | null {
  return tasks.find((task) => task.onboarding && isOpenStatus(task.status)) ?? null
}

/**
 * Should Home offer the welcome page? Only for a task that is about to lock the student's other practice and that they can
 * actually start: not started yet, its test available, and no reason the server has paused the lock.
 */
export function offersWelcome(task: TaskItem | null): task is TaskItem {
  return task !== null && task.status === 'pending' && task.lockOthers && !task.unavailable && !task.lockSuspended && task.test?.available === true
}

/**
 * "Go to your task" from a locked test page: the paper that finishes it when the server named one, else the matching library filter.
 * A lock with neither (a task older than this list, or a leftover platform lock) goes to the full mocks, where every paper that
 * finishes such a lock is listed; never to Home, which has no card for it and would be a loop with no way out.
 */
export function lockTargetPath(data: MyTasks): string {
  const lock = data.lock
  if (!lock) return '/home'
  if (lock.requiredTestId) return `/test/${encodeURIComponent(lock.requiredTestId)}`
  const found = lockedItem(data)
  return found ? libraryFor(found) : libraryFor({ kind: 'full_mock', skill: null })
}
